/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-explicit-any */
import MuiTextField from '@mui/material/TextField'
import {
  ChangeEvent,
  ReactNode,
  Ref,
  useEffect,
  useState,
  KeyboardEvent,
  forwardRef
} from 'react'
import { InputAdornment } from '@mui/material'
import { inputAdornmentStyles, inputStyles } from './TextField.styles'
import IconButton from '../IconButton/IconButton'
import { TextFieldType, textFieldValueType } from '../DynamicForm/DynamicForm'

interface TextFieldProps {
  disabled?: boolean
  placeholder?: string
  label?: string
  field?: any
  hasError?: boolean
  focused?: boolean
  helperText?: string
  type?: TextFieldType
  multiline?: boolean
  id?: string
  flexGrow?: number
  value?: textFieldValueType
  defaultValue?: textFieldValueType
  onChange?: (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void | ((value: string) => void)
  onFocus?: (field: string, value: string) => void
  onBlur?: (field: string, value: string) => void
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
  inputRef?: Ref<any>
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  lines?: number
  isTagInput?: boolean
  maxWidth?: string
  onlyInteger?: boolean
  required?: boolean
}

// A plain string/number adornment ("zł", "%") is wrapped so it gets MUI's
// spacing and the muted unit styling; anything else (icons, buttons) is
// rendered as given.
function renderAdornment(
  adornment: ReactNode,
  position: 'start' | 'end'
): ReactNode {
  if (adornment === undefined || adornment === null || adornment === '') {
    return null
  }

  if (typeof adornment === 'string' || typeof adornment === 'number') {
    return (
      <InputAdornment position={position} sx={inputAdornmentStyles}>
        {adornment}
      </InputAdornment>
    )
  }

  return adornment
}

function sanitizeToPositiveInteger(value: string): string {
  const cleaned = value.replace(/\D+/g, '')

  if (!cleaned) return ''

  const num = Math.max(1, parseInt(cleaned, 10))

  return String(num)
}

const TextField = forwardRef<any, TextFieldProps>((props, ref) => {
  const {
    disabled,
    placeholder,
    label,
    field,
    hasError,
    focused,
    helperText,
    type = 'text',
    multiline = false,
    id,
    flexGrow,
    value,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    inputRef,
    startAdornment,
    endAdornment,
    lines,
    isTagInput = false,
    maxWidth,
    onlyInteger = false,
    required = false
  } = props

  const [showPassword, setShowPassword] = useState(false)
  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  useEffect(() => {
    if (field?.value === undefined || field?.value === '') {
      setShowPassword(false)
    }
  }, [field?.value])

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    let val = e.target.value

    if (type === 'number' && onlyInteger) {
      val = sanitizeToPositiveInteger(val)
    }

    if (val && multiline) {
      val = val.charAt(0).toUpperCase() + val.slice(1)
    }

    e.target.value = val

    field?.onChange(e)

    if (onChange) {
      onChange(e)
    }
  }

  return (
    <MuiTextField
      {...field}
      defaultValue={field?.value || defaultValue}
      value={field?.value || value}
      onChange={handleChange}
      id={id}
      size="small"
      variant="outlined"
      placeholder={placeholder}
      sx={{
        ...inputStyles,
        flexGrow,
        width: flexGrow ? '0' : maxWidth || '100%',
        '.MuiInputBase-root': {
          borderTopLeftRadius: isTagInput ? 0 : '0.25rem',
          borderBottomLeftRadius: isTagInput ? 0 : '0.25rem'
        }
      }}
      disabled={disabled}
      autoComplete="off"
      label={isTagInput ? '' : required && label ? `${label} *` : label}
      error={hasError}
      focused={focused}
      type={type === 'password' && showPassword ? 'text' : type}
      helperText={helperText}
      multiline={multiline}
      rows={multiline ? lines || 5 : 1}
      inputMode={type === 'number' ? 'decimal' : undefined}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      inputRef={ref || inputRef}
      slotProps={{
        /*
          `focused` alone is not enough to keep the label lifted: MUI drops it
          whenever the field is disabled (FormControl computes
          `visuallyFocused !== undefined && !disabled ? visuallyFocused : focusedState`),
          so a disabled empty field let its label fall back inside the box, where
          the language name read as placeholder text rather than as the field's
          name. Shrinking it explicitly is what `focused` is passed for here, and
          it holds whatever the field's focus, value or disabled state is.
        */
        inputLabel: focused ? { shrink: true } : undefined,
        input: {
          endAdornment:
            type === 'password' ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={toggleShowPassword}
                  icon={
                    !showPassword
                      ? 'las la-eye icon-medium'
                      : 'las la-eye-slash icon-medium'
                  }
                  color="primary"
                />
              </InputAdornment>
            ) : (
              renderAdornment(endAdornment, 'end')
            ),
          startAdornment: renderAdornment(startAdornment, 'start')
        }
      }}
    />
  )
})

export default TextField
